import React, { useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import mbxGeocoding from '@mapbox/mapbox-sdk/services/geocoding';
import { MAPBOX_TOKEN } from './mapboxToken';

// Lazy: the Mapbox client validates its token at construction time and
// throws synchronously when the token is missing or malformed. Building it
// at module load means any page that transitively imports this file blows
// up the React tree on first render. Defer to first use so unrelated routes
// (events, services, resources) still render without a token configured.
let geocodingClient = null;
function getGeocodingClient() {
  if (geocodingClient) return geocodingClient;
  if (!MAPBOX_TOKEN) return null;
  try {
    geocodingClient = mbxGeocoding({ accessToken: MAPBOX_TOKEN });
    return geocodingClient;
  } catch (err) {
    console.warn('LocationAutocomplete: Mapbox client init failed', err);
    return null;
  }
}

const LocationAutocomplete = ({ label, value, onChange }) => {
  const [options, setOptions] = useState([]);

  const handleInputChange = async (_, newInputValue, reason) => {
    if (reason !== 'input') return;
    onChange({ location: newInputValue, coords: null });
    if (!newInputValue) {
      setOptions([]);
      return;
    }
    const client = getGeocodingClient();
    if (!client) {
      setOptions([]);
      return;
    }
    try {
      const resp = await client
        .forwardGeocode({ query: newInputValue, autocomplete: true, limit: 5 })
        .send();
      const features = resp.body.features || [];
      setOptions(
        features.map((f) => ({
          label: f.place_name,
          coords: { latitude: f.center[1], longitude: f.center[0] }
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (_, newValue) => {
    if (newValue && typeof newValue === 'object') {
      onChange({ location: newValue.label, coords: newValue.coords });
    }
  };

  return (
    <Autocomplete
      freeSolo
      filterOptions={(x) => x}
      options={options}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
      onInputChange={handleInputChange}
      onChange={handleChange}
      inputValue={value || ''}
      renderInput={(params) => (
        <TextField {...params} label={label} margin="dense" fullWidth size="small" />
      )}
    />
  );
};

export default LocationAutocomplete;
